/**
 * Who is asking.
 *
 * The assistant is the one part of this app that is not anonymous by nature.
 * Everything else - the solver, the fab, the tool panels - is the same for
 * everybody and needs no account. But an inquiry session is a record of what a
 * particular student asked and what came back, and a record needs somebody to
 * belong to. So the assistant, and only the assistant, sits behind a sign-in.
 *
 * TWO DOORS, ON PURPOSE. Google for a student who has a school account and
 * wants the same identity in the next session; anonymous for everyone else -
 * a shared classroom machine, a workshop laptop, a minor whose district will
 * not hand out third-party logins. The anonymous door is not a lesser one: it
 * produces a real Firebase account with a real uid, and the assistant behaves
 * identically behind it. This mirrors what Aladdin does, so a student who has
 * used one Institute app meets the same two choices here.
 *
 * LOADED LATE. The Firebase SDK is about as large as the rest of the interface
 * put together, and a student who never opens a tool panel never needs it. It
 * is therefore behind a dynamic import: the cleanroom renders and the solver
 * ticks with none of it on the page, and `start()` pulls it in once - when the
 * student walks through the airlock, well before they reach a tool.
 *
 * THE OFFLINE COST. This module is the first thing in the app that a dead
 * network can stop. The solver keeps running and the fab stays walkable, but
 * an unreachable Firebase means no sign-in and therefore no assistant. That is
 * inherent in gating on identity; what this module can do is fail legibly
 * rather than silently, which is what `error` and the retry path are for.
 */

import type { Auth as FirebaseAuth, User } from 'firebase/auth'

import { t } from '../i18n/i18n'

/**
 * The public Firebase web config for `simfab-sim`.
 *
 * Deliberately not in a `.env`. Every value here ships inside the JavaScript
 * bundle no matter where it is kept, so hiding it buys nothing and costs a
 * clean checkout the ability to build. The API key is an origin-scoped project
 * identifier, not a credential - access is decided by the Firebase security
 * rules and the authorised-domain list, both of which live server side.
 */
const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyAEriIOa7fqXOYrXcco-XPkmw9aOwrKlHI',
  authDomain: 'simfab-sim.firebaseapp.com',
  projectId: 'simfab-sim',
  storageBucket: 'simfab-sim.firebasestorage.app',
  messagingSenderId: '275067706616',
  appId: '1:275067706616:web:28510b9a5b0da3ef1c2f0d',
}

/** The signed-in student, flattened to the few fields the interface shows. */
export interface Account {
  uid: string
  /** Always something printable; a guest gets a label rather than a name. */
  name: string
  email: string | null
  photoURL: string | null
  anonymous: boolean
}

export type AuthPhase =
  /** The SDK is still loading, or the first auth state has not arrived. */
  | 'loading'
  /** Settled, and nobody is signed in. */
  | 'signed-out'
  /** Settled, and `account` is populated. */
  | 'signed-in'

type AuthModule = typeof import('firebase/auth')

class AuthController {
  /** Null until `start()` has resolved the dynamic import. */
  private fb: { auth: FirebaseAuth; mod: AuthModule } | null = null
  private booting: Promise<void> | null = null
  private readonly listeners = new Set<() => void>()

  phase: AuthPhase = 'loading'
  account: Account | null = null
  /**
   * The last failure, in words a student can act on. Cleared on the next
   * attempt so a stale message never sits under a working button.
   */
  error: string | null = null
  /** True while a sign-in round trip is open, so the buttons can be disabled. */
  pending = false

  /**
   * Load the SDK and begin tracking the auth state. Idempotent and safe to
   * call from several places - the panel calls it defensively, and `main`
   * calls it early so the round trip has finished by the time anyone asks.
   */
  start(): Promise<void> {
    this.booting ??= this.boot()
    return this.booting
  }

  private async boot(): Promise<void> {
    try {
      const [appMod, authMod] = await Promise.all([
        import('firebase/app'),
        import('firebase/auth'),
      ])
      const app = appMod.getApps().length ? appMod.getApp() : appMod.initializeApp(FIREBASE_CONFIG)
      const auth = openAuth(authMod, app)
      this.fb = { auth, mod: authMod }

      // A redirect sign-in finishes on the *next* page load rather than in the
      // click handler that started it, so the result has to be collected here.
      // A failure is not fatal - the student simply lands signed out and can
      // try again - so it must not take the whole boot down with it.
      await authMod.getRedirectResult(auth).catch((err: unknown) => {
        this.error = describe(err)
        return null
      })

      // Resolve on the first callback, then keep listening. Without the first
      // half, `start()` would resolve before we know who is signed in and the
      // panel would flash its sign-in card at a student who is already
      // authenticated; without the second, a sign-out elsewhere would not
      // reach the interface.
      await new Promise<void>((resolve) => {
        authMod.onAuthStateChanged(auth, (user) => {
          this.apply(user)
          resolve()
        })
      })
    } catch (err) {
      // Almost always a dead network. Settle as signed-out with a reason
      // attached rather than leaving the panel spinning forever.
      this.phase = 'signed-out'
      this.error = describe(err)
      this.emit()
    }
  }

  private apply(user: User | null): void {
    this.account = user
      ? {
          uid: user.uid,
          name: user.displayName ?? t(user.isAnonymous ? 'auth.GuestName' : 'auth.SignedIn'),
          email: user.email,
          photoURL: user.photoURL,
          anonymous: user.isAnonymous,
        }
      : null
    this.phase = user ? 'signed-in' : 'signed-out'
    this.pending = false
    this.emit()
  }

  /**
   * Register something that displays auth state. Returns its own unsubscribe,
   * following `onLanguageChange` - a panel that has been torn down must be
   * able to stop being told about sign-ins.
   */
  subscribe(onChange: () => void): () => void {
    this.listeners.add(onChange)
    return () => {
      this.listeners.delete(onChange)
    }
  }

  private emit(): void {
    for (const fn of [...this.listeners]) fn()
  }

  async signInWithGoogle(): Promise<void> {
    await this.attempt(async ({ auth, mod }) => {
      const provider = new mod.GoogleAuthProvider()
      // No scopes beyond the default profile. The app wants a stable identity
      // and nothing else; asking for more would put a consent screen full of
      // permissions in front of a student for no functional gain.
      provider.setCustomParameters({ prompt: 'select_account' })
      try {
        await mod.signInWithPopup(auth, provider)
      } catch (err) {
        const code = codeOf(err)
        // The student changed their mind. Not an error; say nothing.
        if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
          this.pending = false
          this.emit()
          return
        }
        // A blocked popup is common on locked-down school browsers, and a
        // redirect is the flow those browsers do allow. Falling back keeps a
        // classroom working where a bare popup call would just fail.
        if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
          await mod.signInWithRedirect(auth, provider)
          return
        }
        throw err
      }
    })
  }

  async signInAsGuest(): Promise<void> {
    await this.attempt(({ auth, mod }) => mod.signInAnonymously(auth).then(() => undefined))
  }

  async signOut(): Promise<void> {
    await this.attempt(({ auth, mod }) => mod.signOut(auth))
  }

  /** Shared plumbing: clear the last error, mark busy, translate failures. */
  private async attempt(run: (fb: { auth: FirebaseAuth; mod: AuthModule }) => Promise<void>): Promise<void> {
    this.error = null
    this.pending = true
    this.emit()
    try {
      await this.start()
      if (!this.fb) throw new Error(t('auth.ErrUnavailable'))
      await run(this.fb)
    } catch (err) {
      this.error = describe(err)
      this.pending = false
      this.emit()
    }
    // On success `onAuthStateChanged` clears `pending`; on a redirect the page
    // is on its way out and the flag no longer matters.
  }
}

/**
 * Open the auth instance with persistence stated rather than inherited.
 *
 * A signed-in student stays signed in until they sign out - across a reload,
 * a closed tab, and a machine turned off overnight. `getAuth` already does
 * that by default, and the reason not to rely on the default is what happens
 * when it cannot: the SDK quietly drops to the next storage it can reach, and
 * a session that lives only in memory disappears on the next reload with
 * nothing anywhere saying why. On a locked-down school browser, or in a
 * private window, that is the difference between "signs in every visit" and
 * "signs in every time they touch a tool".
 *
 * Stating the chain puts the order in one readable place instead of leaving it
 * to an SDK default that has changed between major versions: IndexedDB first,
 * localStorage when a browser has partitioned it away, and in-memory only as
 * the last resort.
 *
 * `popupRedirectResolver` has to be passed here. `initializeAuth` does not
 * install one by default the way `getAuth` does, and without it
 * `signInWithPopup` fails at the moment a student clicks the button.
 */
function openAuth(mod: AuthModule, app: Parameters<AuthModule['initializeAuth']>[0]): FirebaseAuth {
  try {
    return mod.initializeAuth(app, {
      persistence: [
        mod.indexedDBLocalPersistence,
        mod.browserLocalPersistence,
        mod.inMemoryPersistence,
      ],
      popupRedirectResolver: mod.browserPopupRedirectResolver,
    })
  } catch {
    // Already initialised - a second `boot()` under Vite's hot reload, or
    // another module that reached for `getAuth` first. The existing instance
    // is the one holding the session, so take it rather than throwing.
    return mod.getAuth(app)
  }
}

function codeOf(err: unknown): string {
  return typeof err === 'object' && err !== null && 'code' in err ? String((err as { code: unknown }).code) : ''
}

/**
 * Turn a Firebase error into something a student or an instructor can act on.
 *
 * The cases listed here are the ones that actually happen in a classroom, and
 * each names the thing to change. Anything else falls through to the SDK's own
 * message, which is at least specific enough to search for.
 */
function describe(err: unknown): string {
  switch (codeOf(err)) {
    case 'auth/network-request-failed':
      return t('auth.ErrNetwork')
    case 'auth/operation-not-allowed':
    case 'auth/admin-restricted-operation':
    // Not "the provider is off" but "Authentication was never turned on for
    // this project". Same thing from where the student sits, and the same
    // person has to go and fix it.
    case 'auth/configuration-not-found':
      return t('auth.ErrNotEnabled')
    case 'auth/unauthorized-domain':
      return t('auth.ErrDomain', { host: location.hostname })
    case 'auth/too-many-requests':
      return t('auth.ErrTooMany')
    case 'auth/popup-blocked':
      return t('auth.ErrPopupBlocked')
    default:
      if (err instanceof Error) return err.message
      return String(err)
  }
}

/** One controller for the whole app; every assistant panel reads the same one. */
export const auth = new AuthController()
